var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { throttle, reduce, times, flatten, some, values } from 'lodash';
import DataService from '../../services/DataService';
import { calculateDiscounts } from '../../helpers/packages';
import { isPurchaseRightEntitlement } from '../../helpers/entitlements';
const ds = DataService.getInstance();
// initial state
const state = {
    cartItems: [],
    cartId: '',
    currency: 'USD',
    expireTime: null,
    connectedStripeKeyHash: '',
    totalPackageCost: 0,
    totalActualPackageCost: 0,
    totalAddOnCost: 0,
    shippingAddress: null,
    countries: null,
    loading: {},
    processedCartItems: []
};
// getters
const getters = {
    cartItems: (_state) => _state.cartItems,
    cartId: (_state) => _state.cartId,
    expireTime: (_state) => _state.expireTime,
    currency: (_state) => _state.currency.toLowerCase(),
    countries: (_state) => _state.countries,
    connectedStripeKeyHash: (_state) => _state.connectedStripeKeyHash,
    totalPackageCost: (_state) => _state.totalPackageCost,
    totalAddOnCost: (_state) => _state.totalAddOnCost,
    shippingAddress: (_state) => _state.shippingAddress,
    requiresShippingAddress: (_state) => {
        return some(_state.cartItems, item => {
            const methods = flatten(values(item.itemDelivery));
            return methods.includes('shipped');
        });
    },
    cartTotal: (_state) => {
        return reduce(_state.cartItems, (memo, item) => {
            const totalCost = item.hasZeroDownpayment ? 0 : Math.max(0, item.totalCost - item.discount);
            return memo + (item.quantity * totalCost);
        }, 0);
    },
    cartActualTotal: (_state) => {
        // use totalActualCost instead of totalCost
        return reduce(_state.cartItems, (memo, item) => {
            const totalCost = item.hasZeroDownpayment ? 0 : Math.max(0, item.totalActualCost);
            return memo + (item.quantity * totalCost);
        }, 0);
    },
    cartHasStrikeThroughPrice: (_state) => {
        return (_state.totalActualPackageCost) !== (_state.totalPackageCost + _state.totalAddOnCost);
    },
    cartDiscount: (_state) => {
        return reduce(_state.cartItems, (memo, item) => {
            return memo + item.discount;
        }, 0);
    },
    cartItemLoading: (_state) => (cartId) => {
        return _state.loading[cartId] || false;
    },
    processedCartItems: (_state) => _state.processedCartItems,
};
// actons
const actions = {
    getCart: throttle((_a) => __awaiter(void 0, [_a], void 0, function* ({ dispatch, commit, rootGetters }) {
        const cart = yield ds.getCart();
        if (cart) {
            updateCartState(cart, commit, rootGetters);
        }
        else {
            commit('reset');
        }
    }), 5000, { 'trailing': false }),
    addToCart: (_a, cartItem_1) => __awaiter(void 0, [_a, cartItem_1], void 0, function* ({ commit, rootGetters }, cartItem) {
        const cart = yield ds.addToCart(cartItem);
        if (cart) {
            const cartItems = cart.prettyCartItems;
            const userEntitlements = rootGetters['entitlements/paymentEntitlements'];
            cartItems.forEach(item => {
                item.entitlementInstances = userEntitlements.filter(e => item.entitlements.includes(e.id));
                const [discountPct, discountFixed, hasZeroDownpayment] = calculateDiscounts(item.entitlementInstances);
                item.discountPct = discountPct;
                item.discountFixed = discountFixed;
                item.hasZeroDownpayment = hasZeroDownpayment;
            });
            commit('cartItems', cartItems);
            commit('cartId', cart.cartId);
            commit('currency', cart.currency);
            commit('expireTime', cart.expireTime);
            commit('totalPackageCost', cart.totalPackageCost);
            commit('totalActualPackageCost', cart.totalActualPackageCost);
            commit('totalAddOnCost', cart.totalAddOnCost);
            commit('connectedStripeKeyHash', cart.connectedStripeKeyHash);
        }
        // No need to reset linked package map as user can re-add the same package
    }),
    getCartByForce: () => __awaiter(void 0, void 0, void 0, function* () {
        const cart = yield ds.getCart();
        return cart;
    }),
    getCountries: (_a) => __awaiter(void 0, [_a], void 0, function* ({ commit }) {
        const countries = yield ds.getPrintfulCountries();
        commit('countries', countries);
    }),
    removeItem: (_a, rawCartItem_1) => __awaiter(void 0, [_a, rawCartItem_1], void 0, function* ({ dispatch, commit, rootGetters }, rawCartItem) {
        yield ds.removeItemFromCart(rawCartItem);
        const cart = yield dispatch('getCartByForce');
        cart && (yield updateCartState(cart, commit, rootGetters));
    }),
    changeCartItemQuantity: (_a, _b) => __awaiter(void 0, [_a, _b], void 0, function* ({ commit }, { rawCartItem, quantity }) {
        yield ds.changeCartItemQuantity(rawCartItem, quantity);
        commit('changeCartItemQuantity', { rawCartItem, quantity });
    }),
    freePurchase: (_a) => __awaiter(void 0, [_a], void 0, function* ({ commit, getters }) {
        // TODO: move to server
        const freeCart = getters.cartItems.map((item) => {
            return times(item.quantity, () => {
                let packageId = null;
                if (item.package) {
                    packageId = item.package.objectId || item.package.id;
                }
                let sectionId = null;
                if (item.section) {
                    sectionId = item.section.objectId || item.section.id;
                }
                return {
                    code: null,
                    meal: item.meal,
                    entitlements: item.entitlements,
                    queryString: item.queryString,
                    orderParts: {
                        id: item.id,
                        itemAddOns: item.itemAddOns,
                        itemOptions: item.itemOptions,
                        package_id: packageId,
                        section_id: sectionId,
                        parentItem: item.parentItem,
                    }
                };
            });
        });
        const cart = {
            amount: getters.cartTotal,
            cart: flatten(freeCart)
        };
        yield ds.createOrder(cart);
        yield ds.emptyCart();
    }),
    lockCart: (_a, cartId_1) => __awaiter(void 0, [_a, cartId_1], void 0, function* ({ dispatch, commit }, cartId) {
        yield ds.lockCart(cartId);
        commit('reset');
        yield dispatch('getCart');
    }),
    // removeEntitlementFromCartItem: async ({ commit, getters }, { cartId, entitlementId }) => {
    //   const cartItems = getters.cartItems;
    //   cartItems.forEach((item) => {
    //     if (item.id === cartId) {
    //       const entitlementsInstances = item.entitlementsInstances?.filter((entitlement) => entitlement?.id !== entitlementId);
    //       item.entitlementInstances = entitlementsInstances;
    //       const entitlements = item.entitlements.map((entitlement) => entitlement.id);
    //       item.entitlements = entitlements;
    //     }
    //   });
    //   commit('cartItems', cartItems)
    //   commit('setProcessedCartItems', cartItems)
    // }
};
// mutations
const mutations = {
    reset(state) {
        state.cartItems = [];
        state.cartId = '';
        state.currency = '';
        state.connectedStripeKeyHash = '';
        state.totalPackageCost = 0;
        state.totalActualPackageCost = 0;
        state.totalAddOnCost = 0;
        state.shippingAddress = null;
        state.loading = {};
        state.processedCartItems = [];
    },
    cartItems(state, cartItems) {
        state.cartItems = cartItems;
    },
    cartId(state, cartId) {
        state.cartId = cartId;
    },
    expireTime(state, expireTime) {
        state.expireTime = expireTime;
    },
    currency(state, currency) {
        state.currency = currency;
    },
    totalPackageCost(state, totalPackageCost) {
        state.totalPackageCost = totalPackageCost;
    },
    totalActualPackageCost(state, totalActualPackageCost) {
        state.totalActualPackageCost = totalActualPackageCost;
    },
    totalAddOnCost(state, totalAddOnCost) {
        state.totalAddOnCost = totalAddOnCost;
    },
    removeCartItem(state, rawCartItem) {
        const cartItems = state.cartItems.filter((item) => item.rawItem != rawCartItem);
        state.cartItems = cartItems;
    },
    connectedStripeKeyHash(state, connectedStripeKeyHash) {
        state.connectedStripeKeyHash = connectedStripeKeyHash;
    },
    changeCartItemQuantity(state, cartItemQuantity) {
        const { rawCartItem, quantity } = cartItemQuantity;
        const index = state.cartItems.findIndex((item) => item.rawItem == rawCartItem);
        if (index === -1) {
            throw `Cart Item not found`;
        }
        state.cartItems[index].quantity = quantity;
    },
    shippingAddress(state, address) {
        state.shippingAddress = null;
        state.shippingAddress = address;
    },
    countries(state, countries) {
        state.countries = countries;
    },
    setLoading(state, { cartId, loading }) {
        const cartItems = state.processedCartItems.filter(item => item.groupId === cartId);
        cartItems.forEach(item => {
            state.loading[JSON.parse(item.rawItem).id] = loading;
        });
    },
    setProcessedCartItems(state, cartItems) {
        const cartItemMap = new Map();
        const processedItems = new Set();
        cartItems.forEach((item) => processCartItem(item, cartItemMap, processedItems));
        updateFlags(cartItems, cartItemMap);
        calculateIsLastLinkedCartItem(cartItemMap);
        state.processedCartItems = cartItems.map((item) => {
            const rawItem = JSON.parse(item.rawItem);
            const cartItemFlags = cartItemMap.get(rawItem.id) || {};
            return Object.assign(Object.assign({}, item), cartItemFlags);
        });
    },
};
function processCartItem(item, cartItemMap, processedItems) {
    const rawItem = JSON.parse(item.rawItem);
    if (processedItems.has(rawItem.id))
        return;
    if (!rawItem.parentItem) {
        const groupId = rawItem.id;
        cartItemMap.set(rawItem.id, { isRoot: true, groupId });
        assignGroupToLinkedItems(groupId, rawItem.id, cartItemMap, processedItems);
    }
}
function assignGroupToLinkedItems(groupId, parentId, cartItemMap, processedItems) {
    state.cartItems.forEach((item) => {
        const rawItem = JSON.parse(item.rawItem);
        if (rawItem.parentItem === parentId && !processedItems.has(rawItem.id)) {
            cartItemMap.set(rawItem.id, { isLinkedChild: true, groupId });
            processedItems.add(rawItem.id);
            assignGroupToLinkedItems(groupId, rawItem.id, cartItemMap, processedItems);
        }
    });
}
function updateFlags(cartItems, cartItemMap) {
    cartItems.forEach((item) => {
        const rawItem = JSON.parse(item.rawItem);
        const hasChildren = cartItems.some((child) => {
            const childRawItem = JSON.parse(child.rawItem);
            return childRawItem.parentItem === rawItem.id;
        });
        const flagData = { hasNoLinkedCartItems: !rawItem.parentItem && !hasChildren };
        const existingData = cartItemMap.get(rawItem.id) || {};
        cartItemMap.set(rawItem.id, Object.assign(Object.assign({}, existingData), flagData));
    });
}
function calculateIsLastLinkedCartItem(cartItemMap) {
    const processedCartItems = [...cartItemMap.entries()];
    processedCartItems.forEach(([itemId, currentItem], index) => {
        var _a;
        const isLast = index === processedCartItems.length - 1;
        let isLastLinkedCartItem = false;
        if (isLast) {
            isLastLinkedCartItem = currentItem.isRoot || currentItem.groupId;
        }
        else {
            const nextItem = (_a = processedCartItems[index + 1]) === null || _a === void 0 ? void 0 : _a[1];
            isLastLinkedCartItem =
                currentItem.groupId && nextItem.groupId !== currentItem.groupId;
        }
        cartItemMap.set(itemId, Object.assign(Object.assign({}, currentItem), { isLastLinkedCartItem }));
    });
}
function updateCartState(cart, commit, rootGetters) {
    return __awaiter(this, void 0, void 0, function* () {
        const cartItems = cart.prettyCartItems;
        const userEntitlements = rootGetters['entitlements/paymentEntitlements'];
        cartItems.forEach(item => {
            item.entitlementInstances = userEntitlements.filter(e => item.entitlements.includes(e.id));
            item.entitlementInstances.forEach(entitlement => {
                entitlement.isPurchaseRightEntitlement = isPurchaseRightEntitlement(item.package.id, entitlement);
            });
            const [discountPct, discountFixed, hasZeroDownpayment] = calculateDiscounts(item.entitlementInstances);
            item.discountPct = discountPct;
            item.discountFixed = discountFixed;
            item.hasZeroDownpayment = hasZeroDownpayment;
        });
        commit('cartItems', cartItems);
        commit('setProcessedCartItems', cartItems);
        commit('cartId', cart.cartId);
        commit('currency', cart.currency);
        commit('expireTime', cart.expireTime);
        commit('totalPackageCost', cart.totalPackageCost);
        commit('totalActualPackageCost', cart.totalActualPackageCost);
        commit('totalAddOnCost', cart.totalAddOnCost);
        commit('connectedStripeKeyHash', cart.connectedStripeKeyHash);
        return cart;
    });
}
export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};
